<template>
  <div class="wellness-grid wellness-grid--stagger" v-if="type === 'dashboard'">
    <!-- Mindfulness Card Skeleton -->
    <div class="wellness-card">
      <div class="wellness-card__header">
        <div class="wellness-skeleton wellness-skeleton--title w-32" />
      </div>
      <div class="flex items-center gap-6">
        <div class="wellness-skeleton wellness-skeleton--ring" />
        <div class="space-y-3 flex-1">
          <div class="wellness-skeleton wellness-skeleton--text" />
          <div class="wellness-skeleton wellness-skeleton--text w-1/2" />
          <div class="wellness-skeleton wellness-skeleton--text w-2/3" />
        </div>
      </div>
    </div>

    <!-- Audio Card Skeleton -->
    <div class="wellness-card">
      <div class="wellness-card__header">
        <div class="wellness-skeleton wellness-skeleton--title w-36" />
      </div>
      <div class="flex gap-6 mb-4">
        <div class="wellness-skeleton wellness-skeleton--ring" />
        <div class="wellness-skeleton wellness-skeleton--ring" />
      </div>
      <div class="wellness-skeleton wellness-skeleton--chart" />
    </div>

    <!-- Symptoms Card Skeleton -->
    <div class="wellness-card">
      <div class="wellness-card__header">
        <div class="wellness-skeleton wellness-skeleton--title w-28" />
      </div>
      <div class="flex justify-between mb-4">
        <div class="wellness-skeleton wellness-skeleton--stat" />
        <div class="wellness-skeleton wellness-skeleton--stat" />
      </div>
      <div class="space-y-2">
        <div class="wellness-skeleton h-10 w-full rounded" />
        <div class="wellness-skeleton h-10 w-full rounded" />
        <div class="wellness-skeleton h-10 w-full rounded" />
      </div>
    </div>

    <!-- Insights Card Skeleton -->
    <div class="wellness-card lg:col-span-2">
      <div class="wellness-card__header">
        <div class="wellness-skeleton wellness-skeleton--title w-28" />
      </div>
      <div class="space-y-4">
        <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div class="flex justify-between mb-3">
            <div class="wellness-skeleton h-5 w-16 rounded" />
            <div class="wellness-skeleton h-4 w-20" />
          </div>
          <div class="wellness-skeleton wellness-skeleton--text mb-2" />
          <div class="wellness-skeleton wellness-skeleton--text w-5/6" />
        </div>
        <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div class="flex justify-between mb-3">
            <div class="wellness-skeleton h-5 w-20 rounded" />
            <div class="wellness-skeleton h-4 w-20" />
          </div>
          <div class="wellness-skeleton wellness-skeleton--text mb-2" />
          <div class="wellness-skeleton wellness-skeleton--text w-4/6" />
        </div>
      </div>
    </div>
  </div>

  <!-- Single Card Skeleton -->
  <div v-else-if="type === 'card'" class="wellness-card">
    <div class="wellness-card__header">
      <div class="wellness-skeleton wellness-skeleton--title w-32" />
    </div>
    <div class="space-y-4">
      <div class="flex gap-4">
        <div class="wellness-skeleton wellness-skeleton--stat" />
        <div class="wellness-skeleton wellness-skeleton--stat" />
      </div>
      <div class="wellness-skeleton wellness-skeleton--chart" />
    </div>
  </div>

  <!-- Chart Skeleton -->
  <div v-else-if="type === 'chart'" class="wellness-chart-container">
    <div class="wellness-skeleton wellness-skeleton--chart h-full" />
  </div>

  <!-- Ring Skeleton -->
  <div v-else-if="type === 'ring'" class="flex items-center justify-center">
    <div class="wellness-skeleton wellness-skeleton--ring" />
  </div>

  <!-- Stats Row Skeleton -->
  <div v-else-if="type === 'stats'" class="flex gap-6">
    <div v-for="n in 3" :key="n" class="flex-1">
      <div class="wellness-skeleton wellness-skeleton--stat mx-auto" />
      <div class="wellness-skeleton wellness-skeleton--text mt-2 mx-auto" />
    </div>
  </div>

  <!-- Timeline Skeleton -->
  <div v-else-if="type === 'timeline'" class="space-y-2">
    <div v-for="n in (count || 3)" :key="n" class="wellness-skeleton h-12 w-full rounded" />
  </div>

  <!-- Inline Skeleton -->
  <div v-else class="wellness-skeleton wellness-skeleton--text" :class="sizeClass" />
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'text',
    validator: (val) => ['dashboard', 'card', 'chart', 'ring', 'stats', 'timeline', 'text', 'title'].includes(val)
  },
  count: {
    type: Number,
    default: 3
  },
  size: {
    type: String,
    default: 'md',
    validator: (val) => ['sm', 'md', 'lg'].includes(val)
  }
})

const sizeClass = computed(() => {
  if (props.type === 'title') {
    return 'wellness-skeleton--title'
  }
  const sizes = {
    sm: 'w-1/4',
    md: 'w-1/2',
    lg: 'w-3/4'
  }
  return sizes[props.size]
})
</script>

<style scoped>
/* Additional scoped animations for skeleton shimmer */
.wellness-skeleton {
  position: relative;
  overflow: hidden;
}

.wellness-skeleton::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.dark .wellness-skeleton::after {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
}
</style>
